
import { supabase } from "@/integrations/supabase/client";
import { UserXP, UserBadge, WeeklyChallenge, UserChallengeProgress } from "@/components/community/types";

export async function awardXP(userId: string, xpAmount: number, actionType: string = 'general') {
  try {
    const { error } = await supabase.rpc('award_xp', {
      target_user_id: userId,
      xp_amount: xpAmount,
      action_type: actionType
    });
    
    if (error) throw error;
    console.log(`Awarded ${xpAmount} XP to user ${userId} for ${actionType}`);
  } catch (error) {
    console.error('Error awarding XP:', error);
  }
}

export async function getUserXP(userId: string): Promise<UserXP | null> {
  try {
    const { data, error } = await supabase
      .from('user_xp')
      .select('*')
      .eq('user_id', userId)
      .single();
      
    if (error && error.code !== 'PGRST116') throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user XP:', error);
    return null;
  }
}

export async function getUserBadges(userId: string): Promise<UserBadge[]> {
  try {
    const { data, error } = await supabase
      .from('user_badges')
      .select('*')
      .eq('user_id', userId)
      .order('earned_at', { ascending: false });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user badges:', error);
    return [];
  }
}

export async function getLeaderboard(limit: number = 10): Promise<(UserXP & { member_name: string })[]> {
  try {
    // First get the user XP data
    const { data: xpData, error: xpError } = await supabase
      .from('user_xp')
      .select('*')
      .order('total_xp', { ascending: false })
      .limit(limit);
      
    if (xpError) throw xpError;
    
    // Then get member info for each user
    const leaderboardWithNames = await Promise.all(
      (xpData || []).map(async (xpRecord) => {
        const { data: memberData } = await supabase
          .from('community_members')
          .select('nickname, full_name')
          .eq('id', xpRecord.user_id)
          .single();
        
        return {
          ...xpRecord,
          member_name: memberData?.nickname || memberData?.full_name || `User ${xpRecord.user_id.substring(0, 6)}`
        };
      })
    );
    
    return leaderboardWithNames;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    return [];
  }
}

export async function getActiveWeeklyChallenges(): Promise<WeeklyChallenge[]> {
  try {
    const { data, error } = await supabase
      .from('weekly_challenges')
      .select('*')
      .eq('is_active', true)
      .gte('end_date', new Date().toISOString())
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching challenges:', error);
    return [];
  }
}

export async function getUserChallengeProgress(userId: string): Promise<UserChallengeProgress[]> {
  try {
    const { data, error } = await supabase
      .from('user_challenge_progress')
      .select('*')
      .eq('user_id', userId);
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching challenge progress:', error);
    return [];
  }
}

export function getOrikiTitle(level: number): string {
  switch (level) {
    case 1: return 'Ọmọ Ìlú (Citizen)';
    case 2: return 'Ọmọ Ogun (Warrior)';
    case 3: return 'Ọmọ Ọba (Noble)';
    case 4: return 'Ọgá (Chief)';
    case 5: return 'Ọba (King/Queen)';
    default: return 'Ọmọ Ìlú (Citizen)';
  }
}

export function getXPForAction(action: string): number {
  const xpValues: Record<string, number> = {
    'create_topic': 20,
    'create_reply': 10,
    'create_comment': 5,
    'send_chat': 2,
    'complete_challenge': 50,
    'refer_friend': 25,
    'daily_login': 5,
    'first_topic': 30,
    'first_reply': 15
  };
  
  return xpValues[action] || 5;
}
